/**
 * 个股实时行情 UI 状态 channel：基于 {@link RealtimeSubscriber} 的订阅式实现。
 *
 * 协议要点：
 * - 上行订阅：`{ type: 'stock_market_tips' }`，由 RealtimeSubscriber 在 install 后 5s 轮询重发，与 secChannel 同路径；
 * - 下行：`{ businessType: 'stock_market_tips', data: { status } }`，status 取值见 {@link STOCK_MARKET_TIP_STATUS}；
 * - 下行解析后映射为 `STOCK_REAL_TIME_STATUS` 枚举写入模块级 ref，多个消费者共享同一份状态。
 *
 * 生命周期：
 * - {@link installMarketTipsChannel} 引用计数 +1，首次安装才发起 hub 订阅；
 * - 返回的 `destroy()` 会把计数 -1，全部解绑后释放 hub 订阅、清 timer 并把状态重置为 `NONE`，
 *   避免下次 install 拿到陈旧值。
 *
 * @module utils/realtime/stockChannels/marketTipsChannel
 */

import { isNil, isPlainObject } from 'lodash-es';
import { readonly, ref } from 'vue';
import { RealtimeSubscriber } from '@/utils/realtime/RealtimeSubscriber';
import {
  STOCK_REAL_TIME_STATUS,
  WS_STOCK_MARKET_TIPS,
  mapMarketTipStatusToRealtimeStatus
} from '@/constants/stockRealtimeStatus';

/**
 * 解析 `stock_market_tips` 下行 data 段，映射为 UI 状态。
 * @param {unknown} data 下行消息的 data 段
 * @returns {typeof STOCK_REAL_TIME_STATUS[keyof typeof STOCK_REAL_TIME_STATUS] | null}
 *   data 不合法时返回 null（调用方应忽略，不要写入状态）
 */
function parseMarketTipsPush(data) {
  if (!isPlainObject(data) || isNil(data.tipsType)) {
    return null;
  }
  return mapMarketTipStatusToRealtimeStatus(data.tipsType);
}

/** 模块级单例：最新一次解析得到的 UI 状态 */
const tipStatusRef = ref(STOCK_REAL_TIME_STATUS.NONE);

/** 当前活跃订阅引用计数；归零时释放 subscriber 与状态 */
let refCount = 0;

/** @type {RealtimeSubscriber | null} 模块级 subscriber 实例，install 时构造，destroy 时清掉 */
let subscriberInstance = null;

/**
 * 首次订阅时构造 RealtimeSubscriber 并 install；后续重复调用直接复用，避免重复注册。
 * @returns {void}
 */
function ensureSubscriberInstalled() {
  if (subscriberInstance && subscriberInstance.isInstalled()) {
    return;
  }
  subscriberInstance = new RealtimeSubscriber({
    topic: WS_STOCK_MARKET_TIPS,
    buildSubscribePayload: () => ({ type: WS_STOCK_MARKET_TIPS }),
    onPush: (data) => {
      // RealtimeSubscriber 已做基础 JSON.parse 兜底；这里继续按 tipsType 映射为 UI 状态
      const next = parseMarketTipsPush(data);
      if (next === null) {
        // data 不合法时保留上一次状态，避免 UI 闪烁
        return;
      }
      tipStatusRef.value = next;
    }
  });
  subscriberInstance.install();
}

/**
 * 引用计数归零时释放 subscriber，并把状态重置为 `NONE`，避免下次 install 拿到陈旧值。
 * @returns {void}
 */
function maybeTeardown() {
  if (refCount > 0) {
    return;
  }
  if (subscriberInstance) {
    subscriberInstance.destroy();
    subscriberInstance = null;
  }
  tipStatusRef.value = STOCK_REAL_TIME_STATUS.NONE;
}

/**
 * 安装一次 Market tips channel：引用计数 +1，首次安装才会真正建立 subscriber + hub 订阅。
 *
 * 返回：
 * - `tipStatus`：只读 ref，多个消费者共享同一份状态；
 * - `destroy()`：引用计数 -1；归零时释放 subscriber 并把状态重置为 `NONE`，幂等。
 *
 * @returns {{ tipStatus: Readonly<import('vue').Ref<string>>, destroy: () => void }}
 */
export function installMarketTipsChannel() {
  refCount += 1;
  ensureSubscriberInstalled();
  let disposed = false;
  return {
    tipStatus: readonly(tipStatusRef),
    destroy() {
      // 同一个返回对象的 destroy 重复调用应当幂等，避免错误把计数减成负数
      if (disposed) {
        return;
      }
      disposed = true;
      refCount -= 1;
      maybeTeardown();
    }
  };
}
